import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import '../common/rtCommon.css';
import './ComplaintsReport.css';
import RTPageLayout from '../common/RTPageLayout';

const ComplaintsReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await apiGet('/reports/complaints/');
      if (response.status === 200 && response.data.success) {
        setReportData(response.data.data);
        toastSuccess('Complaints report loaded successfully');
      } else {
        toastError('Failed to load complaints report');
      }
    } catch (error) {
      console.error('Error fetching complaints report:', error);
      toastError('Failed to load complaints report');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchReportData();
  };

  const handleBack = () => {
    navigate('/reports');
  };

  if (loading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="Complaints Report"
        actions={
          <RTButton variant="secondary" onClick={handleBack}>
            &#8592; Back
          </RTButton>
        }
      >
        <RTSpinner message="Loading complaints report..." />
      </RTPageLayout>
    );
  }

  if (!reportData) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="Complaints Report"
        actions={
          <>
            <RTButton variant="secondary" onClick={handleBack}>
              &#8592; Back
            </RTButton>
          </>
        }
      >
        <div className="no-data-message">No data available</div>
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Complaints Report"
      actions={
        <>
          <RTButton variant="secondary" onClick={handleBack}>
            &#8592; Back
          </RTButton>
          <RTButton variant="primary" onClick={handleRefresh}>
            Refresh
          </RTButton>
        </>
      }
    >
      <div className="complaints-report-container">
        {/* Summary Metric */}
        <div className="report-metrics">
          <div className="metric-card">
            <div className="metric-icon">📋</div>
            <div className="metric-value">
              {reportData.total_complaints || 0}
            </div>
            <div className="metric-label">Total Complaints (Last 6 Months)</div>
          </div>
        </div>

        {/* Top 3 Complaint Types */}
        <div className="top-types-section">
          <h3 className="section-title">Top 3 Complaint Types</h3>
          <div className="top-types-grid">
            {reportData.top_complaint_types?.map((type, index) => (
              <div key={index} className="top-type-card">
                <div className="rank-badge">#{index + 1}</div>
                <div className="type-name">{type.complaint_type}</div>
                <div className="type-count">{type.count} complaints</div>
              </div>
            ))}
          </div>
        </div>

        {/* Complaints by Type - Monthly Breakdown */}
        <div className="chart-section">
          <h3 className="section-title">Complaints by Type - Last 6 Months</h3>
          <div className="line-chart-container">
            {(() => {
              // Get all complaint types and months
              const typeEntries = Object.entries(
                reportData.complaints_by_type_monthly || {},
              );
              if (typeEntries.length === 0)
                return (
                  <div className="no-data">No complaint data available</div>
                );

              // Get months from first type (all types have same months)
              const months = typeEntries[0][1].map((d) => d.month);

              // Find global max for scaling
              const allCounts = typeEntries.flatMap(([_, data]) =>
                data.map((d) => d.count),
              );
              const maxCount = Math.max(...allCounts, 1);
              const minCount = Math.min(...allCounts, 0);

              // Colors for different complaint types
              const colors = [
                '#667eea',
                '#11998e',
                '#800020',
                '#0097a7',
                '#ff9800',
                '#153C7F',
                '#f093fb',
                '#43e97b',
              ];

              return (
                <div className="line-chart-wrapper">
                  {/* Chart area */}
                  <svg viewBox="0 0 800 400" className="line-chart">
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line
                        key={`grid-${i}`}
                        x1="60"
                        y1={50 + i * 70}
                        x2="760"
                        y2={50 + i * 70}
                        stroke="#e1e5e9"
                        strokeWidth="1"
                      />
                    ))}

                    {/* Y-axis labels */}
                    {[0, 1, 2, 3, 4].map((i) => {
                      const value = Math.round(maxCount - (i * maxCount) / 4);
                      return (
                        <text
                          key={`y-label-${i}`}
                          x="50"
                          y={55 + i * 70}
                          textAnchor="end"
                          fontSize="12"
                          fill="#6c757d"
                        >
                          {value}
                        </text>
                      );
                    })}

                    {/* X-axis labels */}
                    {months.map((month, i) => (
                      <text
                        key={`x-label-${i}`}
                        x={80 + i * 120}
                        y="380"
                        textAnchor="middle"
                        fontSize="12"
                        fill="#6c757d"
                      >
                        {month}
                      </text>
                    ))}

                    {/* Lines for each complaint type */}
                    {typeEntries.map(([type, data], typeIndex) => {
                      const color = colors[typeIndex % colors.length];
                      const points = data
                        .map((item, i) => {
                          const x = 80 + i * 120;
                          const y =
                            maxCount === 0
                              ? 330
                              : 330 - (item.count / maxCount) * 280;
                          return `${x},${y}`;
                        })
                        .join(' ');

                      return (
                        <g key={typeIndex}>
                          {/* Line */}
                          <polyline
                            points={points}
                            fill="none"
                            stroke={color}
                            strokeWidth="3"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                          />
                          {/* Data points */}
                          {data.map((item, i) => {
                            const x = 80 + i * 120;
                            const y =
                              maxCount === 0
                                ? 330
                                : 330 - (item.count / maxCount) * 280;
                            return (
                              <g key={i}>
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="5"
                                  fill={color}
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <title>{`${type} - ${item.month}: ${item.count} complaints`}</title>
                              </g>
                            );
                          })}
                        </g>
                      );
                    })}
                  </svg>

                  {/* Legend */}
                  <div className="line-chart-legend">
                    {typeEntries.map(([type, _], typeIndex) => {
                      const color = colors[typeIndex % colors.length];
                      return (
                        <div key={typeIndex} className="legend-item">
                          <div
                            className="legend-line"
                            style={{ backgroundColor: color }}
                          ></div>
                          <span className="legend-text">{type}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Two Column Layout for Staff and Building Charts */}
        <div className="charts-row">
          {/* Complaints Resolved by Staff */}
          <div className="chart-section half-width">
            <h3 className="section-title">Complaints Resolved by Staff</h3>
            {reportData.complaints_by_staff &&
            reportData.complaints_by_staff.length > 0 ? (
              <div className="bar-chart">
                {(() => {
                  const counts = reportData.complaints_by_staff.map(
                    (s) => s.resolved_count,
                  );
                  const maxCount = Math.max(...counts, 1);
                  const minCount = Math.min(...counts, 0);

                  return reportData.complaints_by_staff.map((staff, index) => {
                    let heightPercent;
                    if (maxCount === minCount) {
                      heightPercent = 80;
                    } else {
                      const normalized =
                        (staff.resolved_count - minCount) /
                        (maxCount - minCount);
                      heightPercent = 20 + normalized * 75;
                    }

                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-value">{staff.resolved_count}</div>
                        <div
                          className="bar staff-bar"
                          style={{ height: `${heightPercent}%` }}
                          title={`${staff.staff_name}: ${staff.resolved_count} resolved`}
                        ></div>
                        <div className="staff-label">{staff.staff_name}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="no-data">No staff resolution data available</div>
            )}
          </div>

          {/* Top Complaints by Building */}
          <div className="chart-section half-width">
            <h3 className="section-title">Complaints by Building</h3>
            {reportData.top_buildings && reportData.top_buildings.length > 0 ? (
              <div className="bar-chart">
                {(() => {
                  const counts = reportData.top_buildings.map(
                    (b) => b.complaint_count,
                  );
                  const maxCount = Math.max(...counts, 1);
                  const minCount = Math.min(...counts, 0);

                  return reportData.top_buildings.map((building, index) => {
                    let heightPercent;
                    if (maxCount === minCount) {
                      heightPercent = 80;
                    } else {
                      const normalized =
                        (building.complaint_count - minCount) /
                        (maxCount - minCount);
                      heightPercent = 20 + normalized * 75;
                    }

                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-value">
                          {building.complaint_count}
                        </div>
                        <div
                          className="bar building-bar"
                          style={{ height: `${heightPercent}%` }}
                          title={`${building.building_name}: ${building.complaint_count} complaints`}
                        ></div>
                        <div className="building-label">
                          {building.building_name}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="no-data">
                No building complaint data available
              </div>
            )}
          </div>
        </div>
      </div>
    </RTPageLayout>
  );
};

export default ComplaintsReport;
